import { useShallow } from 'zustand/react/shallow'
import { useMergeFields } from './MergeTag'
import { useEffect, useState } from 'react'
import { evaluateFormula, NumberFormat, RoundingOption } from './utils'
import { MergeFieldSetting } from '@web/graphql-types'

type MergeTagOptions = {
  mergeTag: string
  options: {
    formula?: string
    mergeTags?: string[]
    rounding?: RoundingOption
    format?: NumberFormat
  }
}

// this one is used for the html text side of post processing
export const useHtmlProcessMergeTags = (tags: HTMLElement[]) => {
  const [mergeTagOptions, setMergeTagOptions] = useState<Set<MergeTagOptions>>(new Set())
  const mergeTags = useMergeFields(
    useShallow((state) => {
      if (mergeTagOptions.size === 0) {
        return {}
      }

      return [...mergeTagOptions.values()].reduce(
        (acc, { options }) => {
          options.mergeTags?.forEach((tag) => {
            acc[tag] = state.mergeFields.get(tag) || ''
          })
          return acc
        },
        {} as Record<string, string>,
      )
    }),
  )

  useEffect(() => {
    if (tags.length === 0) {
      return
    }

    const mergeTagOptions: Set<MergeTagOptions> = new Set()

    tags.forEach((element) => {
      const tagOptions = JSON.parse(element.getAttribute('data-merge-tag-options') || '{}')

      mergeTagOptions.add({
        mergeTag: element.getAttribute('data-merge-tag-value') || '',
        options: {
          formula: tagOptions.formula,
          mergeTags: tagOptions.mergeTags,
          rounding: tagOptions.rounding,
          format: tagOptions.format,
        },
      })
    })

    setMergeTagOptions(mergeTagOptions)
    useMergeFields
      .getState()
      .addTagsToPostProcess([...mergeTagOptions].map((option) => option.options.mergeTags || [option.mergeTag]).flat())
  }, [tags])

  useEffect(() => {
    if (!(mergeTagOptions.size > 0)) {
      return
    }

    mergeTagOptions.forEach(({ mergeTag, options }) => {
      if (options.formula) {
        const result = evaluateFormula(options.formula, mergeTags, options)
        useMergeFields.getState().setMergeField(mergeTag, result)
      }
    })
  }, [mergeTags])
}

export const useJsProcessMergeTags = (tags: Partial<MergeFieldSetting>[]) => {
  const [mergeTagOptions, setMergeTagOptions] = useState<Set<MergeTagOptions>>(new Set())
  const mergeTags = useMergeFields(
    useShallow((state) => {
      if (mergeTagOptions.size === 0) {
        return {}
      }

      return [...mergeTagOptions.values()].reduce(
        (acc, { options }) => {
          options.mergeTags?.forEach((tag) => {
            acc[tag] = state.mergeFields.get(tag) || ''
          })
          return acc
        },
        {} as Record<string, string>,
      )
    }),
  )

  useEffect(() => {
    if (tags.length === 0) {
      return
    }

    const mergeTagOptions: Set<MergeTagOptions> = new Set()

    tags.forEach((tag) => {
      const tagOptions = JSON.parse(tag.MergeTagOptions || '{}')

      mergeTagOptions.add({
        mergeTag: tag.MergeFieldSettingId || '',
        options: {
          formula: tagOptions.formula,
          mergeTags: tagOptions.mergeTags,
          rounding: tagOptions.rounding,
          format: tagOptions.format,
        },
      })
    })

    setMergeTagOptions(mergeTagOptions)
    useMergeFields
      .getState()
      .addTagsToPostProcess([...mergeTagOptions].map((option) => option.options.mergeTags || [option.mergeTag]).flat())
  }, [tags.length])

  useEffect(() => {
    if (!(mergeTagOptions.size > 0)) {
      return
    }

    mergeTagOptions.forEach(({ mergeTag, options }) => {
      if (options.formula) {
        const result = evaluateFormula(options.formula, mergeTags, options)
        useMergeFields.getState().setMergeField(mergeTag, result)
      }
    })
  }, [mergeTags])
}
